import React, { useState, useEffect, useMemo } from 'react';
import { Table, Badge, Card } from 'react-bootstrap';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { useSelector } from 'react-redux';
import { selectProjectResults } from '../data-access/project.reducer';
import { pickGovernanceEntry } from '../../shared/governanceResultsAccess';
import GovernanceTabLoadingState from '../shared/GovernanceTabLoadingState';
import { pickLatestCollectionRun } from '../../../../utils/collectionRuns';
import { collectSafetyRuns } from '../../AISafety/aiSafety.utils';
import { getLatestPDCRunFromProjectResults, getPDCScoreData } from '../../PromptInjectionPDC/pdc.utils';

interface OntologyTabProps {
  projectId: string;
  userId: string;
  isLoading?: boolean;
  loadingLabel?: string;
}

type OntologyDomainRow = {
  key: string;
  risk_domain: string;
  risk_level: string;
  interpretation?: string | string[];
  references?: Record<string, string>;
  provenance?: string | string[];
};

type WorkbenchRiskOverrides = {
  aiSafety?: string;
  explainability?: string;
  promptInjection?: string;
};

/** Align workbench labels with ontology risk badges (LOW / MEDIUM / HIGH). */
const normalizeOntologyRiskLevel = (raw: unknown): string | null => {
  if (raw == null || raw === '') return null;
  const value = String(raw).trim().toUpperCase();
  if (!value || value === 'UNKNOWN' || value === 'N/A' || value === '—') return null;
  if (value === 'MODERATE' || value === 'REVIEW') return 'MEDIUM';
  if (value === 'APPROVE') return 'LOW';
  if (value === 'REJECT') return 'HIGH';
  if (value === 'CRITICAL' || value === 'HIGH' || value === 'MEDIUM' || value === 'LOW') return value;
  return null;
};

const isBiasFairnessDomain = (key: string, domain: Record<string, unknown>) => {
  const riskDomain = String(domain.risk_domain ?? '');
  return key === 'bias_fairness' || /bias\s*&\s*fairness/i.test(riskDomain);
};

const isContentSafetyDomain = (key: string, domain: Record<string, unknown>) => {
  const riskDomain = String(domain.risk_domain ?? '');
  return (
    key === 'content_safety' ||
    /content\s*safety/i.test(riskDomain) ||
    /ai\s*safety/i.test(riskDomain) ||
    /abuse/i.test(riskDomain)
  );
};

const isExplainabilityDomain = (key: string, domain: Record<string, unknown>) => {
  const riskDomain = String(domain.risk_domain ?? '');
  return key === 'explainability' || /explainability|transparency/i.test(riskDomain);
};

const isInputSecurityDomain = (key: string, domain: Record<string, unknown>) => {
  const riskDomain = String(domain.risk_domain ?? '');
  return (
    key === 'input_security' ||
    /input\s*security/i.test(riskDomain) ||
    /prompt\s*injection/i.test(riskDomain) ||
    /robustness/i.test(riskDomain)
  );
};

const asInterpretationList = (interpretation: unknown): string[] => {
  if (Array.isArray(interpretation)) return interpretation.map(String);
  if (interpretation == null || interpretation === '') return [];
  return [String(interpretation)];
};

const deriveBiasRiskLevel = (domain: Record<string, unknown>): string => {
  const maxDr = Number(domain.max_disparity_ratio ?? 0);
  const llmScore = Number(domain.llm_bias_score ?? 0);
  if ((Number.isFinite(maxDr) && maxDr > 1.15) || (Number.isFinite(llmScore) && llmScore >= 0.8)) {
    return 'HIGH';
  }
  if ((Number.isFinite(maxDr) && maxDr > 1.05) || (Number.isFinite(llmScore) && llmScore >= 0.5)) {
    return 'MEDIUM';
  }
  return 'LOW';
};

const deriveFairnessRiskLevel = (domain: Record<string, unknown>): string => {
  const status = String(domain.fairness_status ?? '').toUpperCase();
  if (status === 'BELOW_THRESHOLD') return 'HIGH';
  if (status === 'ACCEPTABLE') return 'LOW';

  const score = Number(domain.fairness_score ?? 0);
  if (Number.isFinite(score) && score > 0) {
    return score < 0.85 ? 'HIGH' : 'LOW';
  }
  return 'UNKNOWN';
};

/**
 * Ontology backend recomputes AI Safety from score thresholds (any score > 0 → MEDIUM),
 * while workbench shows `risk_tier`. Prefer the workbench / stored tier.
 */
const resolveContentSafetyRiskLevel = (domain: Record<string, unknown>, workbenchAiSafety?: string): string => {
  return (
    normalizeOntologyRiskLevel(domain.llm_abuse_verdict) ??
    normalizeOntologyRiskLevel(domain.risk_tier) ??
    normalizeOntologyRiskLevel(workbenchAiSafety) ??
    String(domain.risk_level ?? 'UNKNOWN')
  );
};

/** Prefer workbench `microeai_explainability.verdict` over ontology stability-threshold scoring. */
const resolveExplainabilityRiskLevel = (domain: Record<string, unknown>, workbenchExplainability?: string): string => {
  return (
    normalizeOntologyRiskLevel(workbenchExplainability) ??
    normalizeOntologyRiskLevel(domain.verdict) ??
    String(domain.risk_level ?? 'UNKNOWN')
  );
};

/**
 * Ontology treats any prompt-injection score > 0 as MEDIUM; workbench uses summary.risk_tier.
 */
const resolveInputSecurityRiskLevel = (domain: Record<string, unknown>, workbenchPromptInjection?: string): string => {
  return (
    normalizeOntologyRiskLevel(workbenchPromptInjection) ??
    normalizeOntologyRiskLevel(domain.risk_tier) ??
    normalizeOntologyRiskLevel(domain.verdict) ??
    String(domain.risk_level ?? 'UNKNOWN')
  );
};

const resolveWorkbenchAiSafetyRisk = (projectResults: unknown): string | undefined => {
  const runs = collectSafetyRuns(projectResults);
  const latest = pickLatestCollectionRun(runs);
  if (!latest) return undefined;
  return (
    normalizeOntologyRiskLevel(latest.risk_tier ?? latest.Risk_Tier) ??
    normalizeOntologyRiskLevel(latest.dataset_verdict ?? latest.Dataset_Verdict) ??
    undefined
  );
};

const resolveWorkbenchExplainabilityRisk = (projectResults: unknown): string | undefined => {
  if (!projectResults || typeof projectResults !== 'object') return undefined;
  const pr = projectResults as Record<string, unknown>;

  const candidates: unknown[] = [];
  for (const rootKey of ['results', 'collection_results', 'feature_results'] as const) {
    const root = pr[rootKey];
    if (!root || typeof root !== 'object') continue;
    const bucket = root as Record<string, unknown>;
    candidates.push(bucket.explainability, bucket.explainability_llm, bucket.explainability_ml);
  }
  candidates.push(pr.explainability, pr.explainability_llm);

  for (const candidate of candidates) {
    const run = pickLatestCollectionRun(candidate as any) as Record<string, unknown> | null;
    if (!run) continue;

    const llmBlock =
      (run.LLM_Model_Explainability as Record<string, unknown> | undefined) ??
      (run.llm_model_explainability as Record<string, unknown> | undefined) ??
      (run.llm_explainability as Record<string, unknown> | undefined) ??
      run;

    const micro =
      (llmBlock.microeai_explainability as Record<string, unknown> | undefined) ??
      (run.microeai_explainability as Record<string, unknown> | undefined);

    // Prefer risk_tier (LOW/MEDIUM/HIGH). `verdict` is often APPROVE/REVIEW/REJECT.
    const verdict =
      normalizeOntologyRiskLevel(run.risk_tier ?? run.Risk_Tier) ??
      normalizeOntologyRiskLevel(llmBlock.risk_tier ?? llmBlock.Risk_Tier) ??
      normalizeOntologyRiskLevel(micro?.risk_tier) ??
      normalizeOntologyRiskLevel(micro?.verdict ?? micro?.Verdict) ??
      normalizeOntologyRiskLevel(run.dataset_verdict ?? run.Dataset_Verdict) ??
      normalizeOntologyRiskLevel(llmBlock.verdict);
    if (verdict) return verdict;
  }

  return undefined;
};

const resolveWorkbenchPromptInjectionRisk = (projectResults: unknown): string | undefined => {
  const run = getLatestPDCRunFromProjectResults(projectResults);
  if (!run) return undefined;

  const scores = getPDCScoreData(run);
  const summary =
    run.summary && typeof run.summary === 'object' && !Array.isArray(run.summary)
      ? (run.summary as Record<string, unknown>)
      : undefined;

  return (
    normalizeOntologyRiskLevel(scores?.riskTier) ??
    normalizeOntologyRiskLevel(summary?.risk_tier ?? summary?.Risk_Tier) ??
    normalizeOntologyRiskLevel(run.risk_tier ?? run.Risk_Tier) ??
    normalizeOntologyRiskLevel(summary?.dataset_verdict ?? summary?.verdict) ??
    undefined
  );
};

/** Expand combined Bias & Fairness API domain into two display rows; align AI Safety / Explainability with workbench. */
const expandOntologyDomains = (
  domains: Record<string, unknown> | null | undefined,
  workbench: WorkbenchRiskOverrides = {}
): OntologyDomainRow[] => {
  if (!domains || typeof domains !== 'object') return [];

  const rows: OntologyDomainRow[] = [];

  for (const [key, value] of Object.entries(domains)) {
    if (!value || typeof value !== 'object') continue;
    const domain = value as Record<string, unknown>;

    if (isBiasFairnessDomain(key, domain)) {
      const interpretations = asInterpretationList(domain.interpretation);
      const biasInterp = interpretations.filter(item => /bias|disparity|\bdr\b/i.test(item));
      const fairnessInterp = interpretations.filter(item => /fairness/i.test(item));
      const references = (domain.references as Record<string, string> | undefined) ?? {};
      const provenance = Array.isArray(domain.provenance)
        ? domain.provenance.map(String)
        : domain.provenance
        ? [String(domain.provenance)]
        : [];

      rows.push({
        key: 'bias',
        risk_domain: 'Bias',
        risk_level: deriveBiasRiskLevel(domain),
        interpretation: biasInterp.length ? biasInterp : interpretations,
        references: {
          ...(references.nist ? { nist: references.nist } : {}),
          ...(references.iso ? { iso: references.iso } : {}),
          ...(references.eeoc ? { eeoc: references.eeoc } : {})
        },
        provenance: provenance.filter(p => /bias/i.test(p)).length
          ? provenance.filter(p => /bias/i.test(p))
          : ['results.bias[*]']
      });

      rows.push({
        key: 'fairness',
        risk_domain: 'Fairness',
        risk_level: deriveFairnessRiskLevel(domain),
        interpretation: fairnessInterp.length ? fairnessInterp : interpretations,
        references: {
          ...(references.eu_ai_act ? { eu_ai_act: references.eu_ai_act } : {}),
          ...(references.nist ? { nist: 'NIST AI RMF – Fairness' } : {})
        },
        provenance: provenance.filter(p => /fairness/i.test(p)).length
          ? provenance.filter(p => /fairness/i.test(p))
          : ['results.fairness[*]']
      });
      continue;
    }

    let riskLevel = String(domain.risk_level ?? 'UNKNOWN');
    if (isContentSafetyDomain(key, domain)) {
      riskLevel = resolveContentSafetyRiskLevel(domain, workbench.aiSafety);
    } else if (isExplainabilityDomain(key, domain)) {
      riskLevel = resolveExplainabilityRiskLevel(domain, workbench.explainability);
    } else if (isInputSecurityDomain(key, domain)) {
      riskLevel = resolveInputSecurityRiskLevel(domain, workbench.promptInjection);
    }

    rows.push({
      key,
      risk_domain: String(domain.risk_domain ?? key),
      risk_level: riskLevel,
      interpretation: domain.interpretation as string | string[] | undefined,
      references: domain.references as Record<string, string> | undefined,
      provenance: domain.provenance as string | string[] | undefined
    });
  }

  return rows;
};

const alignReasoningLayer = (reasoning: unknown, domainRows: OntologyDomainRow[]): string | string[] | null => {
  if (reasoning == null) return null;
  if (!Array.isArray(reasoning)) return String(reasoning);

  const byDomain = new Map(domainRows.map(row => [row.risk_domain.toLowerCase(), row.risk_level]));

  return reasoning
    .map(item => {
      const text = String(item);
      const match = text.match(/^(.+?):\s*(\S+)\s*$/);
      if (!match) return text;
      const domainName = match[1].trim();
      const override = byDomain.get(domainName.toLowerCase());
      if (!override) {
        // Combined "Bias & Fairness" line → leave; Bias/Fairness already split in table.
        if (/bias\s*&\s*fairness/i.test(domainName)) {
          const bias = byDomain.get('bias');
          const fairness = byDomain.get('fairness');
          if (bias && fairness) return [`Bias: ${bias}`, `Fairness: ${fairness}`];
        }
        return text;
      }
      return `${domainName}: ${override}`;
    })
    .flat();
};

const OntologyTab = ({ isLoading = false, loadingLabel }: OntologyTabProps) => {
  const projectResults = useSelector(selectProjectResults);
  const [data, setData] = useState<any | null>(null);

  const ontologyData = useMemo(() => pickGovernanceEntry(projectResults, ['ontology']), [projectResults]);

  useEffect(() => {
    setData(ontologyData ?? null);
  }, [ontologyData]);

  const workbenchOverrides = useMemo<WorkbenchRiskOverrides>(
    () => ({
      aiSafety: resolveWorkbenchAiSafetyRisk(projectResults),
      explainability: resolveWorkbenchExplainabilityRisk(projectResults),
      promptInjection: resolveWorkbenchPromptInjectionRisk(projectResults)
    }),
    [projectResults]
  );

  const domainRows = useMemo(
    () => expandOntologyDomains(data?.domains, workbenchOverrides),
    [data, workbenchOverrides]
  );

  const reasoningLayer = useMemo(
    () => alignReasoningLayer(data?.reasoning_layer, domainRows),
    [data?.reasoning_layer, domainRows]
  );

  const getRiskBadge = (risk: string) => {
    switch (risk?.toUpperCase()) {
      case 'LOW':
        return <Badge bg="success">LOW</Badge>;
      case 'MEDIUM':
      case 'MODERATE':
        return (
          <Badge bg="warning" text="dark">
            MEDIUM
          </Badge>
        );
      case 'HIGH':
        return <Badge bg="danger">HIGH</Badge>;
      case 'CRITICAL':
        return <Badge bg="dark">CRITICAL</Badge>;
      default:
        return <Badge bg="secondary">{risk}</Badge>;
    }
  };

  const getOverallStatusBadge = (status: string) => {
    return status === 'AT_RISK' ? <Badge bg="danger">AT RISK</Badge> : <Badge bg="success">PASS</Badge>;
  };

  return (
    <div className="mb-4">
      <PhoenixDocCard className="mb-4">
        <PhoenixDocCard.Header title="Ontology Analysis (MLP v2)">
          <p className="text-muted mb-0">
            MLP v2 Ontology based on project analysis results (Bias, Fairness, Explainability, etc.).
          </p>
        </PhoenixDocCard.Header>
        <PhoenixDocCard.Body>
          {isLoading && !data && <GovernanceTabLoadingState label={loadingLabel} />}

          {data && (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-4 mb-4 p-3 bg-light rounded border">
                <div>
                  <h5 className="mb-1">Overall Risk</h5>
                  {getOverallStatusBadge(data.overall_risk)}
                </div>
                <div>
                  <h5 className="mb-1">Ontology Version</h5>
                  <span className="fw-bold text-primary">{data.ontology_version || 'N/A'}</span>
                </div>
              </div>

              <h5 className="mb-3">Domain Analysis</h5>
              <div className="table-responsive">
                <Table bordered hover className="align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: '20%' }}>Risk Domain</th>
                      <th style={{ width: '10%' }}>Risk Level</th>
                      <th style={{ width: '30%' }}>Interpretation</th>
                      <th style={{ width: '20%' }}>References</th>
                      <th style={{ width: '20%' }}>Provenance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domainRows.map(domain => (
                      <tr key={domain.key}>
                        <td className="fw-semibold">{domain.risk_domain}</td>
                        <td>{getRiskBadge(domain.risk_level)}</td>
                        <td>
                          {domain.interpretation ? (
                            Array.isArray(domain.interpretation) ? (
                              <ul className="mb-0 ps-3">
                                {domain.interpretation.map((item: string, idx: number) => (
                                  <li key={idx} className="small text-muted">
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="small text-muted">{domain.interpretation}</div>
                            )
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                        <td>
                          {domain.references &&
                            Object.entries(domain.references).map(([refKey, refVal]) => (
                              <div key={refKey} className="mb-1 small">
                                <span className="fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
                                  {refKey}:
                                </span>{' '}
                                <span className="text-muted">{refVal}</span>
                              </div>
                            ))}
                        </td>
                        <td>
                          {domain.provenance ? (
                            Array.isArray(domain.provenance) ? (
                              domain.provenance.map((prov: string, idx: number) => (
                                <div
                                  key={idx}
                                  className="small text-muted font-monospace mb-1"
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {prov}
                                </div>
                              ))
                            ) : (
                              <div className="small text-muted font-monospace mb-1" style={{ fontSize: '0.75rem' }}>
                                {domain.provenance}
                              </div>
                            )
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {reasoningLayer && (
                <div className="mt-4">
                  <h5 className="mb-3">Reasoning Layer</h5>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      {Array.isArray(reasoningLayer) ? (
                        <ul className="mb-0">
                          {reasoningLayer.map((reason: string, idx: number) => (
                            <li key={idx} className="mb-1">
                              {reason}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="mb-0">{reasoningLayer}</div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              )}
            </div>
          )}

          {!isLoading && !data && (
            <div className="text-center py-5 text-muted">
              <h5>No Analysis Data</h5>
              <p>No Ontology analysis data available for this project.</p>
            </div>
          )}
        </PhoenixDocCard.Body>
      </PhoenixDocCard>
    </div>
  );
};

export default OntologyTab;
